import type {
  ReportCatalog,
  ReportQuery,
  ReportQueryResult
} from "@gettactic/api";
import type { QueryClient } from "@tanstack/react-query";
import { type PairedResult, pairedQuery, pairFits } from "../lib/pair";
import { fetchReportQuery, useReportQuery } from "./useReportQuery";

/**
 * The second measure's answer for a paired chart, ready to draw beside the
 * first's. Undefined when nothing is paired, while it loads, or while the
 * two answers do not line up (one of them is still the previous question's).
 */
export const usePairedResult = (
  catalog: ReportCatalog,
  query: ReportQuery | null,
  withKey: string | undefined,
  primary: ReportQueryResult | undefined
): {
  paired: PairedResult | undefined;
  isLoading: boolean;
  isError: boolean;
} => {
  const second = query ? pairedQuery(catalog, query, withKey) : null;
  const measure = second
    ? catalog.measures.find((m) => m.key === second.measure)
    : undefined;
  const { data, isLoading, isFetching, isError } = useReportQuery(second);
  const paired =
    second && measure && data && primary && pairFits(primary, data)
      ? { measure, result: data }
      : undefined;
  return {
    paired,
    isLoading: !!second && !paired && (isLoading || isFetching),
    isError: !!second && isError
  };
};

/**
 * The second measure's answer outside a render (an export): fetched the
 * way the screen fetches it, through the same cache. Undefined when nothing
 * pairs with the query or the answers do not line up.
 */
export const fetchPairedResult = async (
  queryClient: QueryClient,
  catalog: ReportCatalog,
  query: ReportQuery,
  withKey: string | undefined,
  primary: ReportQueryResult
): Promise<PairedResult | undefined> => {
  const second = pairedQuery(catalog, query, withKey);
  const measure = second
    ? catalog.measures.find((m) => m.key === second.measure)
    : undefined;
  if (!second || !measure) {
    return undefined;
  }
  const answer = await fetchReportQuery(queryClient, second);
  return pairFits(primary, answer) ? { measure, result: answer } : undefined;
};
